/**
 * Landing Page Theme — Navy + Gold + White
 * Premium medical SaaS design
 */

// Color Palette
export const COLORS = {
  // Primary — Navy
  primary: '#1e3a5f',
  primaryLight: '#2d5a87',
  primaryDark: '#0f2440',

  // Accent — Gold
  accent: '#f59e0b',
  accentLight: '#fbbf24',
  accentDark: '#d97706',

  // Secondary (kept for feature card colors)
  secondary: '#7928ca',
  cyan: '#00d4ff',
  pink: '#ff0080',
  orange: '#f5a623',
  green: '#00c853',

  // Neutrals — Slate scale
  white: '#ffffff',
  gray50: '#f8fafc',
  gray100: '#f1f5f9',
  gray200: '#e2e8f0',
  gray300: '#cbd5e1',
  gray400: '#94a3b8',
  gray500: '#64748b',
  gray600: '#475569',
  gray700: '#334155',
  gray800: '#1e293b',
  gray900: '#0f172a',
  black: '#000000',

  // Text
  textPrimary: '#0f172a',
  textSecondary: '#475569',
  textMuted: '#64748b',

  // Backgrounds
  bgLight: '#ffffff',
  bgGray: '#f8fafc',

  // Brand
  whatsapp: '#25D366',
  facebook: '#1877f2',
  instagram: '#e4405f',
  discord: '#5865F2',
};

// Gradients
export const GRADIENTS = {
  hero: 'linear-gradient(135deg, #0f2440 0%, #1e3a5f 50%, #2d5a87 100%)',
  footer: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
  heroMesh: `
    radial-gradient(at 27% 37%, rgba(245, 158, 11, 0.08) 0px, transparent 50%),
    radial-gradient(at 97% 21%, rgba(99, 102, 241, 0.06) 0px, transparent 50%),
    radial-gradient(at 52% 99%, rgba(16, 185, 129, 0.05) 0px, transparent 50%)
  `,
  primaryButton: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
  primaryButtonHover: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
  textGradient: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
};

// Shadows
export const SHADOWS = {
  sm: '0 1px 2px rgba(0,0,0,0.05)',
  md: '0 2px 4px rgba(0,0,0,0.1)',
  lg: '0 4px 12px rgba(0,0,0,0.1)',
  button: '0 4px 14px rgba(245, 158, 11, 0.3)',
  card: '0 2px 8px rgba(0,0,0,0.06)',
  cardHover: '0 12px 32px rgba(0,0,0,0.1)',
};

// Transitions
export const TRANSITIONS = {
  fast: 'all 0.15s ease',
  normal: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
};

// Border radius
export const RADIUS = {
  sm: '6px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  full: '9999px',
};

// Generous spacing
export const SPACING = {
  sectionY: { xs: 6, md: 10 },
  sectionGap: { xs: 3, md: 6 },
  cardPadding: { xs: 2.5, md: 3.5 },
  containerPx: { xs: 2, md: 4 },
};

// Larger typography
export const TYPOGRAPHY = {
  h1: { xs: '2.25rem', sm: '2.75rem', md: '3.25rem' },
  h2: { xs: '1.75rem', md: '2.25rem' },
  h3: { xs: '1.25rem', md: '1.5rem' },
  body: '1rem',
  bodyLg: '1.1rem',
  small: '0.875rem',
  tiny: '0.75rem',
};

// Common styles
export const STYLES = {
  // Section container
  section: {
    maxWidth: 1100,
    mx: 'auto',
    px: SPACING.containerPx,
    py: SPACING.sectionY,
  },

  // Primary button — Gold
  primaryButton: {
    background: GRADIENTS.primaryButton,
    color: '#0f2440',
    fontWeight: 700,
    fontSize: '0.9rem',
    px: 3,
    py: 1.2,
    borderRadius: RADIUS.md,
    textTransform: 'none',
    boxShadow: SHADOWS.button,
    transition: TRANSITIONS.normal,
    '&:hover': {
      background: GRADIENTS.primaryButtonHover,
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 20px rgba(245, 158, 11, 0.4)',
    },
  },

  // Secondary button
  secondaryButton: {
    background: 'transparent',
    color: COLORS.textPrimary,
    fontWeight: 600,
    fontSize: '0.9rem',
    px: 3,
    py: 1.2,
    borderRadius: RADIUS.md,
    border: `1.5px solid ${COLORS.gray300}`,
    textTransform: 'none',
    transition: TRANSITIONS.normal,
    '&:hover': {
      borderColor: COLORS.primary,
      color: COLORS.primary,
    },
  },

  // Feature card
  featureCard: {
    p: SPACING.cardPadding,
    borderRadius: RADIUS.xl,
    background: COLORS.white,
    border: `1px solid ${COLORS.gray200}`,
    transition: TRANSITIONS.normal,
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: SHADOWS.cardHover,
      borderColor: 'transparent',
    },
  },

  // Testimonial card
  testimonialCard: {
    p: SPACING.cardPadding,
    borderRadius: RADIUS.xl,
    background: COLORS.white,
    boxShadow: SHADOWS.card,
    transition: TRANSITIONS.normal,
    '&:hover': {
      boxShadow: SHADOWS.cardHover,
    },
  },

  // Gradient text — Gold
  gradientText: {
    background: GRADIENTS.textGradient,
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },

  // Section title — Navy
  sectionTitle: {
    fontSize: TYPOGRAPHY.h2,
    fontWeight: 800,
    color: COLORS.textPrimary,
    letterSpacing: '-0.02em',
    mb: 1.5,
  },

  // Section subtitle
  sectionSubtitle: {
    fontSize: TYPOGRAPHY.body,
    color: COLORS.textSecondary,
    maxWidth: 540,
    mx: 'auto',
    lineHeight: 1.7,
  },
};

// Data
export const STATS = [
  { value: '24/7', label: 'Disponible' },
  { value: '<2s', label: 'Respuesta' },
  { value: '99.9%', label: 'Uptime' },
  { value: 'HIPAA', label: 'Compliant' },
];

export const FEATURES = [
  { title: 'Diagnóstico con IA', description: 'Evalúa síntomas con IA de última generación. Triage inteligente con niveles de urgencia.', icon: 'Psychology', color: '#8b5cf6' },
  { title: 'Agenda de Citas', description: 'Consulta disponibilidad, agenda, cancela y reagenda citas. Todo por WhatsApp, sin llamadas.', icon: 'EventAvailable', color: '#0ea5e9' },
  { title: 'Memoria del Paciente', description: 'Recuerda historial, alergias, medicamentos y conversaciones anteriores de cada paciente.', icon: 'Memory', color: '#10b981' },
  { title: 'Análisis de Imágenes', description: 'Analiza fotos de lesiones, recetas y documentos médicos con visión artificial.', icon: 'ImageSearch', color: '#f59e0b' },
  { title: 'Expediente Médico', description: 'Registro clínico digital integrado. Historial, notas y seguimiento por paciente.', icon: 'MedicalServices', color: '#ec4899' },
  { title: 'Escalamiento al Doctor', description: 'Detecta cuándo se necesita atención humana y transfiere la conversación al doctor.', icon: 'SupportAgent', color: '#6366f1' },
];

export const TESTIMONIALS = [
  { id: 1, name: 'Juan Pérez', role: 'Director de Soporte', company: 'Acme Corp', quote: 'Automatizamos el 80% de consultas. Respuestas inmediatas y el equipo ahorra horas.', avatar: 'https://randomuser.me/api/portraits/men/32.jpg', color: COLORS.primary },
  { id: 2, name: 'María Gómez', role: 'Head of Marketing', company: 'Beta Solutions', quote: 'Integración sencilla. Calificamos leads automáticamente, conversiones +40%.', avatar: 'https://randomuser.me/api/portraits/women/44.jpg', color: COLORS.secondary },
  { id: 3, name: 'Carlos Ruiz', role: 'COO', company: 'Gamma Tech', quote: 'Transformó nuestra atención. Agentes en casos complejos, bot maneja lo rutinario.', avatar: 'https://randomuser.me/api/portraits/men/85.jpg', color: COLORS.pink },
];

export const FAQ_ITEMS = [
  { question: '¿NARA cumple con regulaciones de privacidad médica?', answer: 'Sí. Cumplimos con HIPAA, GDPR, Ley 1581 de 2012 (Colombia) y LFPDPPP (México). Los datos de pacientes están encriptados y cada paciente da su consentimiento explícito antes de interactuar con el asistente.' },
  { question: '¿Puede reemplazar a mi recepcionista?', answer: 'NARA complementa a tu equipo. Atiende consultas fuera de horario, agenda citas automáticamente y filtra urgencias. Tu personal se enfoca en lo que realmente importa: la atención presencial.' },
  { question: '¿Cómo configuro mi chatbot médico?', answer: 'En menos de 15 minutos. Configuras la personalidad del asistente, agregas tus horarios de atención y tu base de conocimiento. Sin código, sin complicaciones.' },
  { question: '¿El diagnóstico por IA es preciso?', answer: 'NARA realiza un triage inicial basado en los síntomas del paciente, no un diagnóstico definitivo. Ordena las posibles causas de más benigna a más seria y recomienda cuándo consultar presencialmente.' },
  { question: '¿Puedo probar NARA antes de contratar?', answer: 'Sí. Solicita una demo gratuita y te activamos una prueba con 20 conversaciones durante 15 días. Nuestro equipo te acompaña en la configuración para que veas cómo NARA interactúa con tus pacientes reales.' },
  { question: '¿Qué pasa si el paciente necesita hablar con el doctor?', answer: 'NARA detecta automáticamente cuándo un paciente necesita atención humana y notifica al doctor por WhatsApp. El doctor puede tomar el control de la conversación en cualquier momento.' },
];

export const INTEGRATIONS = [
  { icon: 'WhatsApp', label: 'WhatsApp', color: COLORS.whatsapp },
  { icon: 'Facebook', label: 'Messenger', color: COLORS.facebook },
  { icon: 'Instagram', label: 'Instagram', color: COLORS.instagram },
  { icon: 'Language', label: 'Website', color: COLORS.primary },
  { icon: 'Api', label: 'API', color: COLORS.secondary },
];

export const NAV_MENUS = {
  producto: [
    { title: 'WhatsApp Chatbot', description: 'Automatiza WhatsApp', icon: 'WhatsApp', color: COLORS.whatsapp, path: '/whatsapp-chatbot' },
    { title: 'Website Chatbot', description: 'Chatbots para web', icon: 'Language', color: COLORS.primary, path: '/constructor' },
    { title: 'API & SDK', description: 'Integraciones', icon: 'Api', color: COLORS.secondary, path: null },
  ],
  soluciones: [
    { title: 'Atención médica', icon: 'MedicalServices', color: COLORS.primary },
    { title: 'Soporte', icon: 'SupportAgent', color: COLORS.secondary },
    { title: 'Atención al cliente', icon: 'PeopleAlt', color: COLORS.cyan },
  ],
  recursos: [
    { title: 'Plantillas', icon: 'ViewModule', color: COLORS.primary },
    { title: 'Tutoriales', icon: 'School', color: COLORS.secondary },
    { title: 'Comunidad', icon: 'Forum', color: COLORS.discord, href: 'https://discord.com/' },
  ],
};

export default { COLORS, GRADIENTS, SHADOWS, TRANSITIONS, RADIUS, SPACING, TYPOGRAPHY, STYLES, STATS, FEATURES, TESTIMONIALS, FAQ_ITEMS, INTEGRATIONS, NAV_MENUS };
